import React, { useState, useEffect } from "react";
import AnneUniversitaireServiceInstance from "service/anneuniversitaire.service";
import ActionBar from "./components/ActionBar";
import AnneUniversitaireTable from "./components/AnneUniversitaireTable";
import AnneUniversitaireFormModal from "./components/AnneUniversitaireFormModal";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import HeaderSection from "components/headers-section";

const AnneUniversitaireManager = () => {
  const [anneesUniversitaires, setAnneesUniversitaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentAnneUniv, setCurrentAnneUniv] = useState({
    id_anneuniv: null,
    dateuniv: "",
    statusuniv: "En cours",
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchAnneesUniversitaires();
  }, []);

  const fetchAnneesUniversitaires = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await AnneUniversitaireServiceInstance.getAll();
      setAnneesUniversitaires(result || []);
    } catch (error) {
      setError(
        "Impossible de charger les années universitaires. Veuillez réessayer plus tard."
      );
      console.error("Error fetching années universitaires:", error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    // Préparer l'année scolaire suivante (e.g., "2025-2026")
    const currentYear = new Date().getFullYear();
    const suggestedYear = `${currentYear}-${currentYear + 1}`;

    setCurrentAnneUniv({
      dateuniv: suggestedYear,
      statusuniv: "En cours",
    });
    setIsEditing(false);
    setIsFormModalOpen(true);
  };

  const openEditModal = (anneUniv) => {
    setCurrentAnneUniv({
      id_anneuniv: anneUniv.id_anneuniv,
      dateuniv: anneUniv.dateuniv,
      statusuniv: anneUniv.statusuniv,
    });
    setIsEditing(true);
    setIsFormModalOpen(true);
  };

  const openDeleteModal = (anneUniv) => {
    setCurrentAnneUniv(anneUniv);
    setIsDeleteModalOpen(true);
  };

  const handleSubmitForm = async (formData) => {
    setLoading(true);
    try {
      if (isEditing) {
        await AnneUniversitaireServiceInstance.update(
          currentAnneUniv.id_anneuniv,
          formData
        );
      } else {
        await AnneUniversitaireServiceInstance.create(formData);
      }

      fetchAnneesUniversitaires();
      setIsFormModalOpen(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        errors: error.response?.data?.errors || {
          general: isEditing
            ? "Impossible de modifier l'année universitaire. Veuillez réessayer."
            : "Impossible de créer l'année universitaire. Veuillez réessayer.",
        },
      };
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await AnneUniversitaireServiceInstance.delete(currentAnneUniv.id_anneuniv);
      fetchAnneesUniversitaires();
      setIsDeleteModalOpen(false);
    } catch (error) {
      setError(
        "Impossible de supprimer l'année universitaire. Veuillez réessayer."
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredAnneesUniversitaires = anneesUniversitaires.filter(
    (anneUniv) =>
      anneUniv.dateuniv.toLowerCase().includes(searchTerm.toLowerCase()) ||
      anneUniv.statusuniv.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-100 px-5 py-8 md:px-8">
      <HeaderSection
          title="Gestion des Années Universitaires"
          description="Créez et gérez les années universitaires"
        />

        <ActionBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onRefresh={fetchAnneesUniversitaires}
          onCreateNew={openCreateModal}
          buttonLabel="Nouvelle Année Universitaire"
          loading={loading}
        />

        {error && (
          <div
            className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600"
            role="alert"
          >
            {error}
          </div>
        )}

        <AnneUniversitaireTable
          anneesUniversitaires={filteredAnneesUniversitaires}
          loading={loading}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
        />

      <AnneUniversitaireFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleSubmitForm}
        initialAnneUniv={currentAnneUniv}
        isEditing={isEditing}
        loading={loading}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        item={currentAnneUniv}
        itemName={currentAnneUniv.dateuniv}
        itemType="année universitaire"
        loading={loading}
      />
    </div>
  );
};

export default AnneUniversitaireManager;
